/**
 * Project Billing Hook
 * 
 * React Query hook for project billing data management
 * Handles caching, loading states, and period management for multiple projects
 */

import { useQuery, UseQueryResult, QueryKey } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { 
  calculateMultiProjectCosts, 
  MultiProjectCostData, 
  validateProjectBillingCalculationInputs 
} from '../utils/projectBillingCalculations';
import { 
  getDefaultPeriodType, 
  getAvailablePeriodTypes, 
  calculatePeriodBounds,
  PeriodData 
} from '../utils/periodCalculations';
import { LLMProject } from '../API';
import { fetchAllLLMProjects } from '../dataaccess/fetchAllLLMProjects';

export interface UseProjectBillingOptions {
  enabled?: boolean;
  usePreview?: boolean;
  refetchInterval?: number; // Auto-refresh interval in ms
}

export interface UseProjectBillingResult {
  // Data
  data: MultiProjectCostData | undefined;
  currentPeriod: PeriodData;
  
  // Loading states
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isFetching: boolean;
  
  // Period management
  selectedPeriodType: string;
  availablePeriodTypes: Array<{ value: string; label: string }>;
  setPeriodType: (periodType: string) => void;
  
  // Actions
  refetch: () => void;
  
  // Validation
  validationErrors: string[];
}

/**
 * Main hook for project billing data across all projects
 */
export function useProjectBilling(
  options: UseProjectBillingOptions = {}
): UseProjectBillingResult {
  
  const {
    enabled = true,
    usePreview = true, // Default to preview for performance
    refetchInterval = 10 * 60 * 1000 // 10 minutes default
  } = options;
  
  // Period management state
  const [selectedPeriodType, setSelectedPeriodType] = useState<string>(getDefaultPeriodType());
  
  // Memoized period calculations
  const currentPeriod = useMemo(() => {
    try {
      return calculatePeriodBounds(selectedPeriodType);
    } catch (error) {
      console.error('Error calculating period bounds:', error);
      return calculatePeriodBounds(getDefaultPeriodType());
    }
  }, [selectedPeriodType]);
  
  const availablePeriodTypes = useMemo(() => getAvailablePeriodTypes(), []);
  
  // Input validation
  const validationErrors = useMemo(() => {
    const validation = validateProjectBillingCalculationInputs('all-projects', selectedPeriodType);
    return validation.errors;
  }, [selectedPeriodType]);
  
  // Query key for React Query caching
  const queryKey: QueryKey = [
    'project-billing-all',
    selectedPeriodType,
    usePreview
  ];
  
  // Main React Query
  const query = useQuery<MultiProjectCostData, Error>({
    queryKey,
    queryFn: async () => {
      // First fetch all projects
      const allProjects = await fetchAllLLMProjects();
      
      // Convert to the format expected by calculateMultiProjectCosts
      const projectsWithData = allProjects.map(project => ({
        id: project.id,
        project: project
      }));
      
      // Calculate costs for all projects
      return calculateMultiProjectCosts(projectsWithData, selectedPeriodType, usePreview);
    },
    enabled: enabled && validationErrors.length === 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)
    refetchInterval: refetchInterval,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry validation errors
      if (error.message.includes('required') || error.message.includes('invalid')) {
        return false;
      }
      // Retry network errors up to 3 times
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
  
  return {
    // Data
    data: query.data,
    currentPeriod,
    
    // Loading states
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    
    // Period management
    selectedPeriodType,
    availablePeriodTypes,
    setPeriodType: setSelectedPeriodType,
    
    // Actions
    refetch: query.refetch,
    
    // Validation
    validationErrors
  };
}

/**
 * Hook for quick project billing preview (limited data for fast loading)
 */
export function useProjectBillingPreview(
  periodType?: string
): Pick<UseProjectBillingResult, 'data' | 'isLoading' | 'isError' | 'error'> {
  
  return useProjectBilling({
    usePreview: true,
    refetchInterval: 0 // No auto-refresh for previews
  });
}
